// NOTE: Cannot use "@app" prefix while the reactor is also being used from 1.0.
//       Once the dependency on the 1.0 dashboard is gone, then we can use all standard features.
import * as d3 from "d3";
import flatten from "lodash/flatten";
import includes from "lodash/includes";
import sortBy from "lodash/sortBy";
import { Bounds, Point, RadianBounds } from "./cartesian";
import {
  AccuracyReactorConfiguration,
  AdequacyReactorConfiguration,
  AttestationReactorConfiguration,
  ReactorConfiguration,
  validateConfiguration,
} from "./configuration";
import Events, { EventType } from "./events";
import {
  BandType,
  bandTypeRenderingIndex,
  bandTypeRenderingOrder,
  StatusType,
  statusTypeRenderingOrder,
} from "./types";
import { AttestationRateDays } from "../projects/enumerators";

// ====================
// Constants
// ====================

// TODO: Consider making this configurable via the ReactorConfiguration.

/** The reference colors used for rendering. Likely will want to change this to CSS. */
const defaultColors: Colors = {
  blue: "#056EDF",
  border: "#AAAAAA",
  green: "#00CD87",
  hover: "#FFF8E1",
  projected: "#00CD87",
  red: "#F23957",
  yellow: "#FFD654",
};

/** The view box defining the bounds of the SVG. */
const viewBox = new Bounds(new Point(0, 0), new Point(100, 100));

/** The total available width of the view box. */
const width = viewBox.higher.x - viewBox.lower.x;

/** The radius of the chart. */
const radius = width / 2;

/** The ratio used to render the inner shape. */
const shapeRatio = 0.5;

/** The inner radius of the chart. Used for text and the inner shape. */
const innerRadius = shapeRatio * radius;

/** The default time to take during a transition (ms). */
const transitionDuration = 750;

/** Triangle size => 4% of the width. */
const triangleSize = 0.04 * width;

/** The portion of the triangle's height to leave rendered when creating the trapezoid. */
const trapezoidRatio = 0.6;

/** The label reference for status types. */
const labelReference: { [K in StatusType]: string } = {
  accuracy: "Accuracy",
  adequacy: "Adequacy",
  attestation: "Attestation",
};

// ====================
// Types
// ====================

interface Colors {
  blue: string;
  border: string;
  green: string;
  hover: string;
  projected: string;
  red: string;
  yellow: string;
}

/** An alias for a d3 selection. */
type RenderRegion = d3.Selection<d3.BaseType, unknown, HTMLElement | null, unknown | undefined>;

/** Contains all necessary data needed to render a band on the SVG. */
interface BandData {
  type: "standard" | "accuracy";
  arcBuilder: d3.Arc<unknown, d3.DefaultArcObject>;
  band: BandType | string;
  endAngle: number;
  group: number;
  innerRadius: number;
  outerRadius: number;
  status: StatusType;
  value: number;
} // TODO: Unify types when arc reactor isn't in use in 1.0.

/** The combined data necessary to render a label. */
class LabelData {
  constructor(
    /** StatusType The status type for the label. */
    public readonly status: StatusType,
    /** number The rotation to apply to the label in radians. */
    private readonly rotationRadians: number,
    /** number The vertical translation to apply to the label. */
    public readonly translation: number = -16,
    /** number Y-axis shift to apply to label. Only used for labels on bottom. */
    public readonly dy: number = 0,
  ) {
    // Make sure the text is rendered correctly oriented so it can be read.
    if (this.rotationRadians === Math.PI) {
      this.rotationRadians = 0;
      this.translation = -1 * this.translation - 1;
      this.dy = 1;
    }
  }

  /** The label's display name. */
  public get label() {
    return labelReference[this.status];
  }

  /** The rotation, in degrees, to apply to the label. */
  public get rotation() {
    return toDegrees(this.rotationRadians);
  }
}

// ====================
// Render
// ====================

/**
 * Renders an Arc Reactor into a given SVG element.
 * @param svg The element to render into.
 * @param config The configuration used to perform the render.
 */
function reactor(svg: SVGSVGElement, config?: ReactorConfiguration): void {
  const colors = { ...defaultColors };

  // If we don't have a configuration then we need to setup one (as well as modify coloring) to dispaly an "uninitialized" arc reactor.
  // Same for if the configuration is invalid.
  const usingFallback = validateConfiguration(config).isLeft();
  if (config && config.accuracy && config.accuracy.isIndeterminant) {
    // Convert colors to grayscale.
    const greyScaleColors = toGreyScale(colors);
    const indeterminantText = config.accuracy.indeterminantText;

    config = {
      accuracy: {
        bands: [
          { color: greyScaleColors.green, name: "", value: 100.0 },
          { color: greyScaleColors.yellow, name: "", value: 100.0 },
          { color: greyScaleColors.red, name: "", value: 100.0 },
        ],
        index: indeterminantText,
      },
      adequacy: config.adequacy,
      attestation: config.attestation,
      composite: indeterminantText,
    };
  } else if (usingFallback) {
    // Convert colors to grayscale.
    const greyScaleColors = toGreyScale(colors);
    const indeterminantText = "TBD";

    config = {
      accuracy: {
        bands: [
          { color: greyScaleColors.green, name: "", value: 100.0 },
          { color: greyScaleColors.yellow, name: "", value: 100.0 },
          { color: greyScaleColors.red, name: "", value: 100.0 },
        ],
        index: indeterminantText,
      },

      adequacy: {
        blue: null,
        green: 100.0,
        index: indeterminantText,
        projected: null,
        projectedIndex: null,
        red: 100.0,
        yellow: 100.0,
      },

      attestation: {
        green: 100.0,
        index: indeterminantText,
        red: 100.0,
        yellow: 100.0,
      },

      composite: indeterminantText,
    };
  }

  const root = d3
    .select(svg)
    .style("width", "100%") // We want to scale based off the width.
    .attr("viewBox", `${viewBox.lower.x} ${viewBox.lower.y} ${viewBox.higher.x} ${viewBox.higher.y}`); // However, we want to operate inside the svg on a 100 point grid.

  // Clear.
  root.selectAll("*").remove();
  d3.selectAll(".overview-trending-charts .arc-reactor div.d3-tooltip").remove();

  // Build out our data set.
  const statusRoutes = findStatuses(config);
  const statuses = statusRoutes.map(([s]) => s);
  const radianBounds = toRadianBounds(statuses);
  const data = createBandData(statuses, config, radianBounds, usingFallback);

  // Prepare the event subsystem.
  // Done here to prevent a memory leak.
  const events = new Events();
  const tooltip = d3.select(".overview-trending-charts .arc-reactor")
    .append("div")
    .classed("d3-tooltip", true)
    .style("opacity", 0)
    .style("left", `70%`)
    .style("top", `30%`);

  // Render.
  const chartRoot = renderChartRoot(root);
  renderHoverRegions(chartRoot.append("g"), colors, statusRoutes, radianBounds, events, tooltip, config.onRouteChange, config.attestation?.attestationRateDays);
  renderChartBands(chartRoot.append("g"), colors, radianBounds, data, config.adequacy?.monochrome, usingFallback);
  renderProjectedTriangles(chartRoot.append("g"), colors, radianBounds, data);
  renderInnerShape(chartRoot.append("g"), colors, statuses, events, config.onRouteChange, config.compositeRoute);
  renderLabels(chartRoot.append("g"), statuses);
  renderComposite(chartRoot.append("g"), config, statuses, events);
}

/**
 * Renders the root of the chart.
 * Everything is based around the centroid.
 * @param svgRoot The SVG root.
 */
function renderChartRoot(svgRoot: RenderRegion) {
  return svgRoot
    .append("g")
    .attr("transform", `translate(${(viewBox.higher.x - viewBox.lower.x) / 2}, ${(viewBox.higher.y - viewBox.lower.y) / 2})`);
}

/**
 * Renders the transparent regions that detect hover events.
 * @param root The root to render against.
 * @param colors The set of colors to utilize while building out the hover regions.
 * @param statuses The set of statuses being rendered.
 * @param radianBounds The bounds for each of the statuses.
 * @param events The events subsystem. Used for emitting.
 * @param onRouteChange In the event a hover region is clicked, this function is called.
 */
function renderHoverRegions(
  root: RenderRegion,
  colors: Colors,
  statuses: Array<[StatusType, string]>,
  radianBounds: RadianBounds,
  events: Events,
  tooltip: RenderRegion,
  onRouteChange: (route: string) => void,
  attestationRateDays?: AttestationRateDays
) {
  root
    .selectAll("path")
    .data(statuses)
    .enter()
    .append("path")
    .on("mouseover", function ([s]) {
      if (s === "attestation" && attestationRateDays) {
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip.html(`<span>Percentage is based on a ${attestationRateDays}-day attestation rate.</span>`);
      }

      d3.select(this)
        .style("opacity", 1);

      events.emit(EventType.Mouseover, s);
    })
    .on("mouseout", function ([s]) {
      tooltip.transition().duration(500).style("opacity", 0);

      d3.select(this)
        .style("opacity", 0);

      events.emit(EventType.Mouseout, s);
    })
    .on("mouseup", ([, route]) => {
      if (route && onRouteChange) {
        onRouteChange(route);
      }
    })
    .attr("fill", colors.hover)
    .attr("fill-rule", "evenodd")
    .style("opacity", 0)
    .style("cursor", "pointer")
    .attr("d", ([status], i) => {
      const bounds = radianBounds[status];

      const si = point(bounds.lower, innerRadius); // Start Inner
      const so = point(bounds.lower, radius); // Start Outer
      const eo = point(bounds.higher, radius); // End Outer
      const ei = point(bounds.higher, innerRadius); // End Inner

      // Handle the special cases for 1 and 2.
      let first: Point;
      let second: Point;

      switch (statuses.length) {
        case 1:
          first = point(2 * Math.PI / 3, innerRadius);
          second = point(4 * Math.PI / 3, innerRadius);

          // The region is a circle with the triangle cut out.
          return `
            M 0 ${-innerRadius}
            L 0 ${-radius}
            A ${radius} ${radius} 0 0 1 0 ${radius}
            A ${radius} ${radius} 0 0 1 0 ${-radius}
            L 0 ${-innerRadius}
            L ${first.x} ${first.y}
            L ${second.x} ${second.y}
            Z`;

        case 2:
          const [isTrap] = isTrapezoid(statuses.map(([s]) => s));
          if (isTrap) {
            if (status === StatusType.Accuracy || status === StatusType.Adequacy) {
              // The region is a trapezoid.
              const originalTriangleHeight = innerRadius + innerRadius * Math.sin(Math.PI / 6);
              const newHeight = trapezoidRatio * originalTriangleHeight;

              first = new Point(si.x + newHeight / Math.tan(Math.PI / 3), si.y - newHeight);
              second = new Point(ei.x - newHeight / Math.tan(Math.PI / 3), si.y - newHeight);

              return `
              M ${first.x} ${first.y}
              L ${si.x} ${si.y}
              L ${so.x} ${so.y}
              A ${radius} ${radius} 0 0 1 0 ${-radius}
              A ${radius} ${radius} 0 0 1 ${eo.x} ${eo.y}
              L ${ei.x} ${ei.y}
              L ${second.x} ${second.y}
              Z`;
            }
          } else {
            const h = innerRadius * Math.sin(Math.PI / 6);
            const direction = i % 2 === 0 ? 1 : -1;
            const base = innerRadius * Math.cos(Math.PI / 6);

            return `
              M 0 ${-innerRadius}
              L 0 ${-radius}
              A ${radius} ${radius} 0 ${direction === 1 ? 0 : 1} ${direction === 1 ? 1 : 0} 0 ${radius}
              L 0 ${h}
              L ${direction * base} ${h}
              Z`;
          }
      }

      // All other cases...
      return `
        M ${si.x} ${si.y}
        L ${so.x} ${so.y}
        A ${radius} ${radius} 0 0 1 ${eo.x} ${eo.y}
        L ${ei.x} ${ei.y}
        Z`;
    });
}

/**
 * Renders the bands of the chart.
 * @param chartRoot The chart root.
 * @param colors The set of colors to use while rendering the bands.
 * @param radianBounds The bounds, defined in radians, for the bands.
 * @param data The data used to render.
 * @param grayscaleAlreadyApplied Whether a grayscale transform has already been applied to the colors.
 */
function renderChartBands(
  chartRoot: RenderRegion,
  colors: Colors,
  radianBounds: RadianBounds,
  data: BandData[],
  grayscaleAdequacy: boolean,
  grayscaleAlreadyApplied: boolean,
) {
  const colorTransform = (d: BandData) => {
    const c = colors[d.band as BandType];
    return (d.status === StatusType.Attestation || d.status === StatusType.Adequacy && grayscaleAdequacy) &&
      !grayscaleAlreadyApplied ? rgbToHex(grayscale(hexToRgb(c))) : c;
  };
  const color = (d: BandData) => d.type === "standard"
    ? colorTransform(d)
    : d.band;

  chartRoot
    .selectAll("path")
    .data(data)
    .enter()
    .append("path")
    .attr("transform", (d) => {
      const bounds = radianBounds[d.status];
      return `rotate(${toDegrees(bounds.lower)})`;
    })
    .attr("d", (d) => d.arcBuilder(null))
    .attr("fill", color) // DAH: Temporary, remove the grayscale override when breakdown by G/Y/R is available.
    .style("pointer-events", "none")
    .transition()
    .duration(transitionDuration)
    .attrTween("d", (d: BandData) => {
      const interpolate = d3.interpolate(0, d.endAngle);
      return (tick: number) => {
        const endAngle = interpolate(tick);
        return d.arcBuilder
          .endAngle(endAngle)(null);
      };
    });
}

/**
 * Renders the triangles at the end of the projected lines.
 * @param root The root we're rendering into.
 * @param colors The set of colors to use when rendering the project triangles.
 * @param radianBounds The set of bounds to use during the rendering.
 * @param data The set of band data.
 */
function renderProjectedTriangles(root: RenderRegion, colors: Colors, radianBounds: RadianBounds, data: BandData[]) {
  // We need to find the projected value's triangles.
  // Thus, we create a data set we can bind to.
  const projected = data
    .filter((d) => d.band === BandType.Projected && d.value)
    .map((d) => {
      const coords = point(d.endAngle, d.innerRadius);
      const triangle = d3.symbol()
        .type(d3.symbolTriangle)
        .size(0);

      return {
        coords,
        endAngle: d.endAngle,
        group: d.group,
        status: d.status,
        triangle,
      };
    })
    .filter((d) => d);

  if (projected.length > 0) {
    root
      .selectAll("g")
      .data(projected)
      .enter()
      .append("g")
      .attr("transform", (d) => {
        const bounds = radianBounds[d.status];
        return `rotate(${toDegrees(bounds.lower)}) translate(${d.coords.x}, ${d.coords.y})`;
      })
      .append("path")
      .attr("d", (d) => d.triangle(null))
      .attr("fill", colors.green)
      .attr("transform", (d) => `rotate(${toDegrees(d.endAngle)}) translate(-0.2, 1.3)`) // Fudge factor to embed the triangle slightly inside.
      .style("pointer-events", "none")
      .transition()
      .delay(transitionDuration)
      .duration(0.15 * transitionDuration)
      .attrTween("d", (d) => {
        const interpolate = d3.interpolate(0, triangleSize);

        return (t: number) => {
          return d.triangle.size(interpolate(t))(null);
        };
      });
  }
}

function renderInnerShape(
  root: RenderRegion,
  colors: Colors,
  statuses: StatusType[],
  events: Events,
  onRouteChange?: (route: string) => void,
  compositeRoute?: string,
) {
  // a, b, c represent the three vertices of a triangle starting from the top and moving clockwise.
  const a = point(0, innerRadius);
  const b = point(2 * Math.PI / 3, innerRadius);
  const c = point(4 * Math.PI / 3, innerRadius);

  // d, e, f represent those same three vertices expanded out to the full radius.
  const d = point(0, radius);
  const e = point(2 * Math.PI / 3, radius);
  const f = point(4 * Math.PI / 3, radius);

  let path: string;
  switch (statuses.length) {
    case 1:
      path = `
        M ${a.x} ${a.y}
        L ${d.x} ${d.y}
        L ${a.x} ${a.y}
        L ${b.x} ${b.y}
        L ${c.x} ${c.y}
        Z`;
      break;

    case 2:
      const [isTrap] = isTrapezoid(statuses);
      if (isTrap) {
        // The region is a trapezoid.
        const originalTriangleHeight = innerRadius + innerRadius * Math.sin(Math.PI / 6);
        const newHeight = trapezoidRatio * originalTriangleHeight;

        const first = { x: b.x - newHeight / Math.tan(Math.PI / 3), y: b.y - newHeight };
        const second = { x: c.x + newHeight / Math.tan(Math.PI / 3), y: c.y - newHeight };

        path = `
          M ${first.x} ${first.y}
          L ${b.x} ${b.y}
          L ${e.x} ${e.y}
          L ${b.x} ${b.y}
          L ${c.x} ${c.y}
          L ${f.x} ${f.y}
          L ${c.x} ${c.y}
          L ${second.x} ${second.y}
          Z`;
      } else {
        const h = innerRadius * Math.sin(Math.PI / 6);

        path = `
          M 0 ${-innerRadius}
          L 0 ${-radius}
          L 0 ${-innerRadius}
          L ${b.x} ${b.y}
          L 0 ${h}
          L 0 ${radius}
          L 0 ${h}
          L ${c.x} ${c.y}
          Z`;
      }
      break;

    case 3:
      path = `
        M ${a.x} ${a.y}
        L ${d.x} ${d.y}
        L ${a.x} ${a.y}
        L ${b.x} ${b.y}
        L ${e.x} ${e.y}
        L ${b.x} ${b.y}
        L ${c.x} ${c.y}
        L ${f.x} ${f.y}
        L ${c.x} ${c.y}
        Z`;
      break;

    default:
      break;
  }

  const shape = root
    .append("path")
    .attr("d", path)
    .attr("stroke", colors.border)
    .attr("stroke-width", 0.25)
    .attr("fill", "transparent")
    .style("cursor", "pointer")
    .on("mouseover", function () {
      d3.select(this)
        .attr("fill", colors.hover);
    })
    .on("mouseout", function () {
      d3.select(this)
        .attr("fill", "transparent");
    })
    .on("mouseup", () => {
      if (compositeRoute && onRouteChange) {
        onRouteChange(compositeRoute);
      }
    });

  events.registerListener(EventType.Mouseover, () => {
    shape.attr("fill", colors.hover);
  });

  events.registerListener(EventType.Mouseout, () => {
    shape.attr("fill", "transparent");
  });
}

function renderLabels(root: RenderRegion, statuses: StatusType[]) {
  const data: LabelData[] = [];
  switch (statuses.length) {
    case 1:
      // No exterior labels.
      break;

    case 2:
      const [isTrap, [statusOne, statusTwo]] = isTrapezoid(statuses);
      if (isTrap) {
        data.push(new LabelData(statusOne, 0, -14.0));
        data.push(new LabelData(statusTwo, 0, 15.5));
      } else {
        statuses.forEach((s, i) => {
          data.push(new LabelData(s, (Math.PI / 3) + (i * 4 * Math.PI / 3)));
        });
      }
      break;

    case 3:
      statuses.forEach((s, i) => {
        data.push(new LabelData(s, (Math.PI / 3) + (i * 2 * Math.PI / 3)));
      });
      break;

    default:
      break;
  }

  root
    .selectAll("g")
    .data(data)
    .enter()
    .append("g")
    .attr("transform", (d) => `rotate(${d.rotation})`)
    .append("text")
    .text((d) => d.label)
    .attr("transform", (d) => `translate(0, ${d.translation})`)
    .attr("text-anchor", "middle")
    .attr("dy", (d) => `${d.dy}px`)
    .attr("alignment-baseline", "central")
    .style("pointer-events", "none")
    .style("font-size", "4px")
    .style("fill", "rgba(0, 0, 0, 0.7)");
}

function renderComposite(root: RenderRegion, config: ReactorConfiguration, statuses: StatusType[], events: Events) {
  // We apply a slight fudge factor to improve the look of the trapezoid.
  if (isTrapezoid(statuses)) {
    root.attr("transform", "translate(0, 2)");
  }

  let composite: number = config.composite as number;
  let text = "Composite";
  if (statuses.length === 1) {
    composite = config[statuses[0]].index as number;
    text = labelReference[statuses[0]];
  } else if (config.compositeOverride) {
    composite = config[config.compositeOverride].index as number;
    text = labelReference[config.compositeOverride];
  }

  const num = root
    .append("text")
    .text(typeof (composite) === "number" ? composite.toFixed(1) : composite)
    .attr("text-anchor", "middle")
    .attr("alignment-baseline", "top")
    .style("font-size", "7px")
    .style("font-weight", "bold")
    .style("fill", "rgba(0, 0, 0, 0.7)");

  const label = root
    .append("text")
    .text(text)
    .attr("transform", "translate(0, 2)")
    .attr("dy", "1px")
    .attr("text-anchor", "middle")
    .attr("alignment-baseline", "hanging")
    .style("font-size", "3.5px")
    .style("fill", "rgba(0, 0, 0, 0.7)");

  events.registerListener(EventType.Mouseover, (s) => {
    let labelText: string | number = (config[s] as AdequacyReactorConfiguration).projectedIndex;
    if (labelText) {
      labelText = `${typeof (labelText) === "number" ? labelText.toFixed(1) : labelText} (projected)`;
    } else {
      labelText = labelReference[s];
    }

    num.text(
      `${typeof config[s].index === "number" ?
        (config[s].index as number).toFixed(1) :
        config[s].index}${config[s].isPercent ? "%" : ""}`,
    );
    label.text(labelText);
  });

  events.registerListener(EventType.Mouseout, () => {
    // Reset
    num.text(typeof (composite) === "number" ? composite.toFixed(1) : composite);
    label.text(text);
  });
}

// ====================
// Helpers
// ====================

/**
 * Finds the statuses that are going to be rendered.
 * Attaches the, possible falsey, route as well.
 * @param config The reactor configuration.
 */
function findStatuses(config: ReactorConfiguration): Array<[StatusType, string]> {
  const statuses: Array<[StatusType, string]> = [];

  if (config.adequacy) {
    statuses.push([StatusType.Adequacy, config.adequacy.route]);
  }
  if (config.accuracy) {
    statuses.push([StatusType.Accuracy, config.accuracy.route]);
  }
  if (config.attestation) {
    statuses.push([StatusType.Attestation, config.attestation.route]);
  }

  return sortBy(statuses, [([s]) => statusTypeRenderingOrder[s]]);
}

/**
 * Finds the band types to render based off a given status type.
 * @param status The status.
 * @param config The configuration the Arc Reactor is being built from.
 */
function findBands(status: StatusType, config: ReactorConfiguration): Array<BandType | string> {
  let bands: Array<BandType | string> = [];

  switch (status) {
    case StatusType.Accuracy:
      bands = config.accuracy.bands.map((b) => b.color);
      break;
    case StatusType.Adequacy:
      bands = [BandType.Green, BandType.Yellow, BandType.Red, BandType.Blue, BandType.Projected];
      break;
    case StatusType.Attestation:
      bands = [BandType.Green, BandType.Yellow, BandType.Red];
      break;
    default:
      throw new Error(`Unknown status type "${status}"`);
  }

  return status === StatusType.Accuracy
    ? bands
    : sortBy(bands, [(b: BandType) => bandTypeRenderingOrder[b]]);
}

/**
 * Builds the band data used in the d3 rendering process.
 * @param statuses The set of statuses being rendered.
 * @param config The configuration passed in by the caller.
 * @param radianBounds The bounds, defined in radians, utilized during rendering.
 */
function createBandData(statuses: StatusType[], config: ReactorConfiguration, radianBounds: RadianBounds, usingFallback: boolean): BandData[] {
  return flatten(statuses.map((status, groupIndex) => {
    const bounds = radianBounds[status];
    const bands = findBands(status, config);

    const scale = status === StatusType.Accuracy && !usingFallback
      ? d3.scaleLinear()
        .domain([0, d3.sum((config[status] as AccuracyReactorConfiguration).bands.map((b) => b.value))])
        .range([0, 100])
      : d3.scaleLinear()
        .domain([0, 100])
        .range([0, 100]);

    return bands.map((band, i) => {
      const renderingIndex = status === StatusType.Accuracy
        ? i
        : bandTypeRenderingIndex[band as BandType];

      // TODO: Make the thickness and padding configurable.
      let thickness = 4;

      // Edge Case: Projected is a thin line on top of the standard green line.
      if (band === BandType.Projected) {
        thickness = 0.75;
      }

      // Radius Continued...
      const padding = renderingIndex * 2;
      const outer = radius - padding - thickness * renderingIndex;
      const inner = outer - thickness;

      let value: number = status === StatusType.Accuracy
        ? (config[status] as AccuracyReactorConfiguration).bands
          .find(({ color: c }) => c === band).value
        : (config[status])[band as keyof (AdequacyReactorConfiguration | AttestationReactorConfiguration)] as number;

      const valueResult = scale(value);
      value = valueResult > 0 && valueResult < 0.1 ? 0.9 : valueResult; // 0.9 is the smallest visible band size in the Project overview and Project Tile

      // Edge Case: Combine green and blue to produce the value that blue *actually* renders.
      if (band === BandType.Blue) {
        if (status === StatusType.Adequacy || status === StatusType.Attestation) {
          value = value + (config)[status][BandType.Green];
        } else {
          value = value + config[status].bands.find((b) => b.color === BandType.Green).value;
        }
      }

      // Calculate the start and end angles.
      const radianSize = bounds.higher - bounds.lower;
      const endAngle = radianSize * value / 100;

      const arcBuilder = d3.arc()
        .outerRadius(outer)
        .innerRadius(inner)
        .startAngle(0)
        .endAngle(0);

      const res: BandData = {
        arcBuilder,
        band,
        endAngle,
        group: groupIndex,
        innerRadius: inner,
        outerRadius: outer,
        status,
        type: status === StatusType.Accuracy ? "accuracy" : "standard",
        value,
      };

      return res;
    });
  }));
}

function toGreyScale(colors: Colors): Colors {
  for (const colorKey of Object.keys(colors)) {
    const color = (colors)[colorKey as keyof Colors];
    const rgb = grayscale(hexToRgb(color));
    (colors)[colorKey as keyof Colors] = rgbToHex(rgb);
  }
  return colors;
}

/**
 * Provides the radian bounds for each status type.
 * @param types The set of types being rendered.
 */
function toRadianBounds(types: StatusType[]): RadianBounds {
  const bounds: RadianBounds = {
    accuracy: null,
    adequacy: null,
    attestation: null,
  };

  // Determine the set of bounds we have to choose from.
  switch (types.length) {
    case 0:
      bounds.adequacy = new Bounds(0, 0);
      bounds.accuracy = new Bounds(0, 0);
      bounds.attestation = new Bounds(0, 0);
      break;

    case 1:
      bounds.adequacy = new Bounds(0, 2 * Math.PI);
      bounds.accuracy = new Bounds(0, 2 * Math.PI);
      bounds.attestation = new Bounds(0, 2 * Math.PI);
      break;

    case 2:
      bounds.adequacy = new Bounds(0, Math.PI);
      bounds.accuracy = new Bounds(Math.PI, 2 * Math.PI);
      bounds.attestation = new Bounds(Math.PI, 2 * Math.PI);

      // Special Case: Accuracy and Attestation
      const [isTrap, [boundOne, boundTwo]] = isTrapezoid(types);
      if (isTrap) {
        bounds[boundOne] = new Bounds(-2 * Math.PI / 3, 2 * Math.PI / 3);
        bounds[boundTwo] = new Bounds(2 * Math.PI / 3, 4 * Math.PI / 3);
      }
      break;

    case 3:
    default:
      bounds.adequacy = new Bounds(0, (2 * Math.PI) / 3);
      bounds.accuracy = new Bounds((4 * Math.PI) / 3, 2 * Math.PI);
      bounds.attestation = new Bounds((2 * Math.PI) / 3, (4 * Math.PI) / 3);
      break;
  }

  return bounds;
}

/**
 * Determines whether we're dealing with the rendering of a trapezoid.
 */
function isTrapezoid(types: StatusType[]): [boolean, StatusType[]] {
  const isTrap = types.length === 2
    && (includes(types, StatusType.Adequacy) || includes(types, StatusType.Accuracy))
    && includes(types, StatusType.Attestation);
  return [isTrap, [
    includes(types, StatusType.Adequacy) ? StatusType.Adequacy : StatusType.Accuracy,
    StatusType.Attestation,
  ]];
}

/**
 * Finds a point on a circle, given an angle theta and a radius r.
 * @param theta The angle.
 * @param r The radius.
 */
function point(theta: number, r: number) {
  const x = r * Math.sin(theta);
  const y = -r * Math.cos(theta);

  return new Point(x, y);
}

/**
 * Converts a radian value to a degree value.
 * @param radians The radian value to convert.
 */
function toDegrees(radians: number) {
  return radians * 180 / Math.PI;
}

/**
 * Parses a six digit hex color into its RGB components.
 * Returned as a length three array.
 * @param hex The hex value to parse.
 */
function hexToRgb(hex: string): [number, number, number] {
  const m = hex.match(/^#([0-9a-f]{6})$/i)[1];
  return [
    parseInt(m.substr(0, 2), 16),
    parseInt(m.substr(2, 2), 16),
    parseInt(m.substr(4, 2), 16),
  ];
}

/**
 * Converts a three length array of RGB values into a hex color string.
 * @param rgb The RGB color to convert.
 */
function rgbToHex(rgb: [number, number, number]) {
  let s = "#";

  for (const part of rgb) {
    let hex = Math.round(part).toString(16);

    if (hex.length < 2) {
      hex = "0" + hex;
    }

    s += hex;
  }

  return s;
}

/**
 * Applies a grayscale transform to an RGB color.
 * @param rgb The color.
 */
function grayscale(rgb: [number, number, number]): [number, number, number] {
  const [r, g, b] = rgb.map((c) => c / 255);
  const linear = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const gray = linear <= 0.0031308
    ? 12.92 * linear
    : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;

  return [gray * 255, gray * 255, gray * 255];
}

// Exports
export default reactor;
